import { useMemo } from 'react';
import {
  i18nInit,
  useLanguageControlOverrides,
  useLoadLocale
} from '@elfsight/lib-i18n';
import { LOCAL_WIDGET_ID, WidgetTokenProvider } from '@elfsight/lib-utils';
import {
  getVisibilityOnPageSettings,
  VisibilityOnDevicesProvider,
  VisibilityOnPagesWrapper
} from '@elfsight/widget-conditional-display';
import { useCustomJS, useDefineAPISchema } from '@elfsight/widget-js-api';
import { PortalNestProvider } from '@elfsight/widget-modal';
import { CustomCSS, createCustomCSSPrefixClassName } from '@elfsight/widget-ui';
import { StorageProvider, useComputedFont } from '@elfsight/widget-utils';
import { I18nProvider } from '@lingui/react';
import { useSettings } from '@wu/widget-core';
import CATALOG from '../i18n/languages/widget/catalog.json';
import { meta } from './meta';
import type { AccessibilitySettings } from './types';
import { A11yStateProvider } from './widget/providers/a11y-state';
import { DemoToggleProvider } from './widget/providers/demo-toggle';
import { NavigationProvider } from './widget/providers/navigation';
import { PageContextProvider } from './widget/providers/page';
import { ThemeProvider } from './widget/providers/theme';
import { Widget } from './widget/widget';

export function Accessibility(_settings: AccessibilitySettings) {
  const {
    language,
    visibilityOnDevices,
    customCSS,
    customJS,
    editMode,
    renderThumbnailMode,
    widgetId = 'test-widget-id',
    widgetToken,
    ...settings
  } = useSettings();

  const i18n = useMemo(() => i18nInit(), []);

  const customCSSPrefixClassName = createCustomCSSPrefixClassName(
    meta.appAlias,
    widgetId
  );
  const messages = useLoadLocale(language?.language, 'widget', i18n);
  const [computedFontRef, computedFont] = useComputedFont();

  useLanguageControlOverrides({
    language,
    messages,
    catalog: CATALOG,
    i18n
  });
  useDefineAPISchema();
  useCustomJS(customJS, editMode || renderThumbnailMode);

  return (
    <WidgetTokenProvider
      widgetToken={widgetToken}
      widgetId={widgetId || LOCAL_WIDGET_ID}
    >
      <I18nProvider i18n={i18n}>
        <VisibilityOnDevicesProvider
          widgetDisplayMode={visibilityOnDevices}
          editMode={editMode}
        >
          <VisibilityOnPagesWrapper
            widgetVisibilityOnPages={getVisibilityOnPageSettings(settings)}
            editMode={editMode}
          >
            <StorageProvider widgetId={widgetId} name={meta.appName}>
              <PortalNestProvider
                customCSSClassName={customCSSPrefixClassName}
                font={{ value: computedFont }}
              >
                <NavigationProvider>
                  <PageContextProvider>
                    <ThemeProvider>
                      <DemoToggleProvider>
                        <A11yStateProvider>
                          <CustomCSS
                            customCSS={customCSS}
                            prefixClassName={customCSSPrefixClassName}
                          />
                          <Widget ref={computedFontRef} />
                        </A11yStateProvider>
                      </DemoToggleProvider>
                    </ThemeProvider>
                  </PageContextProvider>
                </NavigationProvider>
              </PortalNestProvider>
            </StorageProvider>
          </VisibilityOnPagesWrapper>
        </VisibilityOnDevicesProvider>
      </I18nProvider>
    </WidgetTokenProvider>
  );
}
